import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { servicesQuery } from '@/services/api';
import { useForm } from '@/components/form';
import { useStore } from '@tanstack/react-form';
import { useSnackbars } from '@customerio/pluma-components/react';
import { CUSTOM_INDUSTRY_OPTION_VALUE } from '@/features/business-profile-forms';
import { formOpts, toAIContextOverrides, toFormData, type FormData } from './helpers/form';
import type { AIContextOverrides, AIContext } from './types';
import { useRef, useCallback } from 'react';
import { generate } from '@customerio/hydra-copilot';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

export function useAIContextOverrides() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  return servicesQuery.getAIContextOverrides.useQuery({
    queryKey: ['aiContextOverrides', workspaceId],
    queryData: {
      params: { workspaceId: workspaceId ?? 0 },
    },
    enabled: workspaceId != null && workspaceId > 0,
  });
}

export function useUpdateAIContextOverrides() {
  const query = servicesQuery.useQueryClient();
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const snackbars = useSnackbars();

  const updateMutation = servicesQuery.updateAIContextOverrides.useMutation({
    onSuccess: () => {
      query.invalidateQueries({ queryKey: ['aiContextOverrides', workspaceId] });
      snackbars.create({
        message: 'Business context updated',
      });
    },
  });

  return {
    updateAIContextOverrides: async (overrides: AIContextOverrides) => {
      if (workspaceId == null || workspaceId <= 0) {
        throw new Error('Invalid workspace ID. Cannot update AI context overrides without a valid workspace.');
      }

      await updateMutation.mutateAsync({ params: { workspaceId }, body: { overrides } });
    },
  };
}

export interface ChangeInfo {
  fieldsChangedCount: number;
  fieldsAddedCount: number;
  fieldsEditedCount: number;
  fieldsClearedCount: number;
  changedFields: string[];
}

// Helper to calculate simple field changes for analytics
function calculateFieldChanges(initialData: FormData, currentData: FormData): ChangeInfo {
  const changedFields: string[] = [];
  let added = 0;
  let edited = 0;
  let cleared = 0;

  for (const [key, newVal] of Object.entries(currentData)) {
    const oldVal = initialData[key as keyof FormData];
    const oldStr = String(oldVal ?? '');
    const newStr = String(newVal ?? '');

    if (oldStr !== newStr) {
      changedFields.push(key);
      if (newStr === '') cleared++;
      else if (oldStr === '') added++;
      else edited++;
    }
  }

  return {
    fieldsChangedCount: changedFields.length,
    fieldsAddedCount: added,
    fieldsEditedCount: edited,
    fieldsClearedCount: cleared,
    changedFields,
  };
}

/**
 * Custom hook for managing Business context form state.
 */
export function useAIBusinessProfileForm(
  aiContext?: AIContext,
  overrides?: AIContextOverrides,
  onSave?: (changeInfo: ChangeInfo & { editSessionMs: number }) => void,
) {
  const { updateAIContextOverrides } = useUpdateAIContextOverrides();
  const editStartTimeRef = useRef<number | null>(null);
  const initialFormDataRef = useRef(toFormData(aiContext, overrides));

  const startEditSession = useCallback(() => {
    editStartTimeRef.current ??= Date.now();
  }, []);

  const form = useForm({
    ...formOpts,
    defaultValues: {
      ...toFormData(aiContext, overrides),
    },
    onSubmit: async ({ value }) => {
      const editSessionMs = editStartTimeRef.current !== null ? Date.now() - editStartTimeRef.current : 0;
      const changeInfo = calculateFieldChanges(initialFormDataRef.current, value);
      await updateAIContextOverrides(toAIContextOverrides(value));
      onSave?.({ ...changeInfo, editSessionMs });

      form.reset(value);

      editStartTimeRef.current = null;
      initialFormDataRef.current = value;
    },
  });

  const { isDirty, isValid } = useStore(form.store, (state) => ({ isDirty: state.isDirty, isValid: state.isValid }));

  return {
    form,
    isDirty,
    isValid,
    startEditSession,
  };
}

export function useTransformMessageByBrandVoice({
  message,
  formData,
  channel,
}: {
  message: string;
  formData: FormData;
  channel: string;
}) {
  const { formalityScore, humorScore, respectScore, energyScore, industry, customIndustry, ...restFormValues } =
    formData;
  const resolvedIndustry = industry === CUSTOM_INDUSTRY_OPTION_VALUE ? customIndustry : industry;
  const businessProfileContextForPrompt = { ...restFormValues, industry: resolvedIndustry };

  const PREVIEW_IN_CHAT_INSTRUCTIONS = `Apply the brand voice to the sample ${
    channel === 'sms' ? 'SMS' : channel
  } message and give back a unique transformed message based on the following:

   The tone scores:

   ${JSON.stringify(
     {
       formalityScore,
       humorScore,
       respectScore,
       energyScore,
     },
     null,
     2,
   )}

   The business profile context:

    ${JSON.stringify(businessProfileContextForPrompt, null, 2)}

   The message we are transforming:

    ${message}
    `;

  return useMutation({
    mutationKey: ['transform-message-by-brand-voice'],
    mutationFn: async () => {
      const response = await generate(
        PREVIEW_IN_CHAT_INSTRUCTIONS,
        z.toJSONSchema(
          z.object({
            message: z.string().describe('The message text to transform to match the brand voice'),
            channel: z.enum(['email', 'sms', 'push', 'in-app']).optional().describe('Optional channel hint'),
          }),
        ),
      );
      return (response?.message as string) ?? '';
    },
  });
}
